import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { playlistApi } from '../services/apiService';

interface Playlist {
  id: string;
  name: string;
  song_count: number;
  updated_at: string;
}

const Playlists: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newName, setNewName] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
      return;
    }
    if (user) {
      playlistApi.list()
        .then(data => setPlaylists(data.playlists))
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, [user, loading, navigate]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const { playlist } = await playlistApi.create(newName.trim());
      setPlaylists(prev => [{ ...playlist, song_count: 0, updated_at: new Date().toISOString() }, ...prev]);
      setNewName('');
      setShowCreateInput(false);
    } catch (err: any) {
      showToast(err.message);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    try {
      await playlistApi.delete(id);
      setPlaylists(prev => prev.filter(p => p.id !== id));
      showToast(`"${name}" deleted`);
    } catch (err: any) {
      showToast(err.message);
    }
  };

  if (loading || isLoading) {
    return <div style={containerStyle}><p style={textStyle}>Loading...</p></div>;
  }

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>My Playlists</h1>

      {toast && <div style={toastStyle}>{toast}</div>}

      {showCreateInput ? (
        <div style={createContainerStyle}>
          <input
            type="text"
            placeholder="Playlist name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            style={inputStyle}
            autoFocus
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleCreate}
              disabled={!newName.trim()}
              style={{ ...actionBtnStyle, opacity: !newName.trim() ? 0.5 : 1 }}
            >
              Create
            </button>
            <button onClick={() => { setShowCreateInput(false); setNewName(''); }} style={cancelBtnStyle}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowCreateInput(true)} style={createBtnStyle}>+ New Playlist</button>
      )}

      {playlists.length === 0 ? (
        <p style={textStyle}>No playlists yet. Create one to get started!</p>
      ) : (
        <div style={listStyle}>
          {playlists.map(pl => (
            <div key={pl.id} style={itemStyle}>
              <Link to={`/playlists/${pl.id}`} style={linkStyle}>
                <span style={nameStyle}>{pl.name}</span>
                <span style={countStyle}>{pl.song_count} song{pl.song_count !== 1 ? 's' : ''}</span>
              </Link>
              <button onClick={() => handleDelete(pl.id, pl.name)} style={deleteBtnStyle}>&times;</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const containerStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  minHeight: '80vh', padding: '2rem 1rem', color: '#b08a8a', width: '100%',
  boxSizing: 'border-box',
};

const titleStyle: React.CSSProperties = { color: '#815353', marginBottom: '1.5rem' };
const textStyle: React.CSSProperties = { color: '#815353' };

const toastStyle: React.CSSProperties = {
  background: '#3d2a2a', color: '#b08a8a', padding: '10px 20px',
  borderRadius: '8px', fontSize: '0.85rem', textAlign: 'center',
  border: '1px solid #815353', marginBottom: '1rem',
};

const createContainerStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: '8px',
  marginBottom: '2rem', width: '100%', maxWidth: '300px',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 16px', borderRadius: '25px', border: 'none',
  background: '#3d2a2a', color: '#fff', fontSize: '1rem', boxSizing: 'border-box',
};

const actionBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: 'none',
  background: '#815353', color: '#fff', fontWeight: 'bold', fontSize: '0.9rem',
  cursor: 'pointer',
};

const cancelBtnStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: '20px', border: '1px solid #815353',
  background: 'transparent', color: '#815353', fontSize: '0.9rem', cursor: 'pointer',
};

const createBtnStyle: React.CSSProperties = {
  padding: '10px 24px', borderRadius: '25px', border: '1px solid #b08a8a',
  background: 'transparent', color: '#b08a8a', fontWeight: 'bold', fontSize: '1rem',
  cursor: 'pointer', marginBottom: '2rem',
};

const listStyle: React.CSSProperties = {
  width: '100%', maxWidth: '500px',
};

const itemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', padding: '12px 16px',
  borderBottom: '1px solid rgba(129,83,83,0.3)', transition: 'background 0.2s',
};

const linkStyle: React.CSSProperties = {
  flex: 1, textDecoration: 'none', display: 'flex', flexDirection: 'column', gap: '4px',
};

const nameStyle: React.CSSProperties = { color: '#fff', fontSize: '1.1rem' };
const countStyle: React.CSSProperties = { color: '#815353', fontSize: '0.85rem' };

const deleteBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: '#815353', fontSize: '1.5rem',
  cursor: 'pointer', padding: '0 8px',
};

export default Playlists;
